// A job's photos and files (attachments.list, as the app's PhotoStripProps; pages/ux-full
// components/jobs.js: PhotoStrip) as PhotoTiles' tiles and RowList's rows (BL-189 consolidation; was
// JobViews' PhotoStrip): what each says, in words, with no drawing of its own.
// - Over them: how many the customer sees, and how many aren't ready to show yet (dashed under).
// - Each photo: its kind, then its note (or, with none, the name made up for it: never its file's name,
//   P-0161); who took it and when; whether the customer sees it, and the one action on it, Hide or Show,
//   only where the screen allows it. A video's or a document's visibility isn't changed here.
// - Its other files (a message's wrapper, a contact card): a row each, its name a link (the file's own
//   name on hover only), its type at the end.
import type { ReactNode } from 'react';
import { Label } from '../components/Label/Label.tsx';
import { Link } from '../components/Link/Link.tsx';
import type { Photo } from '../components/PhotoThumb/PhotoThumb.tsx';
import type { Tile } from '../components/PhotoTiles/PhotoTiles.tsx';
import type { Row } from '../components/RowList/RowList.tsx';
import type { PhotoStripProps } from '../job-schemas.ts';

type StripPhoto = PhotoStripProps['photos'][number];

/** Its words for a screen reader and the "Open … full size" button: its note, else its kind. */
export const photoWhat = (p: StripPhoto) =>
  p.caption ?? `${p.tag} ${p.tile.t === 'video' ? 'video' : 'photo'}`;

function photoOf(p: StripPhoto): Photo {
  const t = p.tile;
  const caption = photoWhat(p);
  if (t.t === 'picture') return { id: p.id, src: t.src, caption };
  if (t.t === 'video') return { id: p.id, src: '', caption, kind: 'video' };
  if (t.t === 'file')
    return { id: p.id, src: '', caption, kind: 'file', face: t.words, ...(t.href ? { href: t.href } : {}) };
  return { id: p.id, src: '', caption, waiting: t.words };
}

/** "2 of 6 the customer sees; 1 not ready to show yet." Null with none. */
export function photoLede(data: PhotoStripProps): ReactNode {
  const n = data.photos.length;
  if (!n) return null;
  const seen = data.photos.filter((p) => p.shared).length;
  const waiting = data.photos.filter((p) => p.tile.t === 'waiting').length;
  return (
    <>
      {seen} of {n} the customer sees
      {waiting > 0 && (
        <>
          ; <Label kind="wait">{waiting} not ready to show yet</Label>
        </>
      )}
      .
    </>
  );
}

export function photoTiles(
  data: PhotoStripProps,
  o: {
    /** The tiles open the viewer: each is named "Open … full size". */
    opens?: boolean;
    /** Show a photo to the customer, or keep it in the office (attachments.setVisibility): the screen's. */
    onShare?: (id: string, shared: boolean) => void;
    /** A change is on its way: nothing can be pressed twice. */
    busy?: boolean;
  } = {},
): Tile[] {
  return data.photos.map((p) => {
    const what = photoWhat(p);
    const opens = o.opens && (p.tile.t === 'picture' || p.tile.t === 'video');
    return {
      photo: photoOf(p),
      // its kind and its note, else its made-up name ("Before 2"): never its file's name
      kicker: p.caption ? `${p.tag}:` : (p.name ?? p.tag),
      ...(p.caption ? { name: p.caption } : {}),
      lines: [
        ...(p.by ? [p.by] : []),
        // who sees it, said as a word; the change is its action
        ...(p.canShare
          ? [
              <Label key="who" kind={p.shared ? 'now' : 'muted'}>
                {p.shared ? 'Customer sees it' : 'Office only'}
              </Label>,
            ]
          : []),
      ],
      ...(p.canShare && o.onShare
        ? {
            actions: [
              {
                id: 'share',
                text: p.shared ? 'Hide' : 'Show',
                label: `${p.shared ? 'Hide from the customer' : 'Show to the customer'}: ${what}`,
                onPress: () => o.onShare?.(p.id, !p.shared),
                ...(o.busy ? { isDisabled: true } : {}),
              },
            ],
          }
        : {}),
      ...(opens ? { openLabel: `Open ${what} full size` } : {}),
    };
  });
}

/** Its other files, a row each: the name a link to open it, its type at the end. */
export const photoFileRows = (data: PhotoStripProps): Row[] =>
  data.files.map((f) => ({
    id: f.id,
    name: f.href ? (
      <span {...(f.file ? { title: f.file } : {})}>
        <Link href={f.href} isExternal>
          {f.name}
        </Link>
      </span>
    ) : (
      f.name
    ),
    end: { aside: f.type },
  }));
